import React, { useState, useEffect } from 'react'
import { Card, CardContent, Stack, Table, TableBody, TableCell, TableHead, TableRow, Button } from '@mui/material'
import { styled } from '@mui/material/styles'
import { AggregationType, MetricAggregationDto, getMetric } from 'api/metrics'
import axios from 'axios'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import FileSaver from "file-saver"

interface KmChatStats {
  request: number
  thumb_up: number
  thumb_down: number
  comment: number
}

const MetricsTable = ({ companyId }: { companyId: string }) => {

  const [metrics, setMetrics] = useState<MetricAggregationDto[]>([])
  const [tabletVisitMetrics, setTabletVisitMetrics] = useState<MetricAggregationDto[]>([])
  const [kmChatStats, setKmChatStats] = useState<MetricAggregationDto[]>([])

  useEffect(() => {

    const fetchData = async () => {
      try {
        const [tcCntMetric, tcAvgMetric, tcRecycledSum, tcMTSum, tcTargetTextCopyCntMetric, tcTargetTextCopyWithEditCntMetric, tcAssetCreatedMetric, tcAssetCreatedWithEditMetric, jobCreatedCntMetric, editorOpenedCntMetric] = await Promise.all([
          getMetric("PORTAL_TRANSLATE", companyId, AggregationType.CNT),
          getMetric("PORTAL_TRANSLATE", companyId, AggregationType.AVG),
          getMetric("PORTAL_TRANSLATE", companyId, AggregationType.CNT, "RECYCLED", "true"),
          getMetric("PORTAL_TRANSLATE", companyId, AggregationType.CNT, "RECYCLED", "false"),
          getMetric("PORTAL_TARGET_TEXT_COPY", companyId, AggregationType.CNT),
          getMetric("PORTAL_TARGET_TEXT_COPY", companyId, AggregationType.CNT, "EDITED_MANUALLY", "true"),
          getMetric("PORTAL_ASSET_CREATED", companyId, AggregationType.CNT),
          getMetric("PORTAL_ASSET_CREATED", companyId, AggregationType.CNT, "EDITED_MANUALLY", "true"),
          getMetric("PORTAL_JOB_CREATED", companyId, AggregationType.CNT),
          getMetric("PORTAL_EDITOR_OPENED", companyId, AggregationType.CNT)
        ])        
        const [biVisited, discoverMoreVisited, tcVisited, instantVisited, enhanceVisited, trustmineVisited, exfluencyAskVisited, exfluencyCreateVisited] = await Promise.all([
          getMetric("PORTAL_TABLET_VISITED", companyId, AggregationType.CNT, "TABLET", "BI"),
          getMetric("PORTAL_TABLET_VISITED", companyId, AggregationType.CNT, "TABLET", "DISCOVER_MORE"),
          getMetric("PORTAL_TABLET_VISITED", companyId, AggregationType.CNT, "TABLET", "TRANSLATE_AND_CREATE"),
          getMetric("PORTAL_TABLET_VISITED", companyId, AggregationType.CNT, "TABLET", "INSTANT"),
          getMetric("PORTAL_TABLET_VISITED", companyId, AggregationType.CNT, "TABLET", "ENHANCE"),
          getMetric("PORTAL_TABLET_VISITED", companyId, AggregationType.CNT, "TABLET", "TRUSTMINE"),
          getMetric("PORTAL_TABLET_VISITED", companyId, AggregationType.CNT, "TABLET", "EXFLUENCY ASK"),
          getMetric("PORTAL_TABLET_VISITED", companyId, AggregationType.CNT, "TABLET", "EXFLUENCY CREATE")
        ])
        const tcRecycledVsMTRatio: MetricAggregationDto =
        {
          name: "AI Text Translator recycled to MT ratio",
          recentValue: safeDivide(tcRecycledSum.recentValue, tcMTSum.recentValue),
          totalValue: safeDivide(tcRecycledSum.totalValue, tcMTSum.totalValue)
        }
        const allMetrics = [
          { ...tcCntMetric, name: "AI Text Translator translation count" },
          { ...tcAvgMetric, name: "AI Text Translator translation avg word count" },
          { ...tcRecycledVsMTRatio },
          { ...tcTargetTextCopyCntMetric, name: "AI Text Translator target text copy count" },
          { ...tcTargetTextCopyWithEditCntMetric, name: "AI Text Translator target text copy with edit count" },
          { ...tcAssetCreatedMetric, name: "AI Text Translator asset created count" },
          { ...tcAssetCreatedWithEditMetric, name: "AI Text Translator asset created with edit count" },
          { ...jobCreatedCntMetric, name: "Job created from Portal count" },
          { ...editorOpenedCntMetric, name: "Editor opened from Portal count" }
        ]
        const tabletVisitMetrics = [
          { ...instantVisited, name: "AI File Translator tablet visit count" },
          { ...enhanceVisited, name: "Editor tablet visit count" },
          { ...trustmineVisited, name: "Editor+ tablet visit count" },
          { ...exfluencyAskVisited, name: "Exfluency Ask tablet visit count" },
          { ...exfluencyCreateVisited, name: "Exfluency Create tablet visit count" },
          { ...biVisited, name: "BI tablet visit count" },
          { ...discoverMoreVisited, name: "Discover More tablet visit count" },
          { ...tcVisited, name: "AI Text Translator tablet visit count" }
        ]
        setMetrics(allMetrics)
        setTabletVisitMetrics(tabletVisitMetrics)
      } catch (error) {
        console.error('Error fetching data: ', error)
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    axios.get<KmChatStats>(`/km/api/km/chat/stats?company_id=${companyId}`).then((response) => {
      const total = response.data
      axios.get<KmChatStats>(`/km/api/km/chat/stats?company_id=${companyId}&days=30`).then((response) => {
        const last30Days = response.data
        setKmChatStats([
          { recentValue: last30Days.comment, totalValue: total.comment, name: "Comments" },
          { recentValue: last30Days.thumb_up, totalValue: total.thumb_up, name: "Thumb Up" },
          { recentValue: last30Days.thumb_down, totalValue: total.thumb_down, name: "Thumb Down" },
          { recentValue: last30Days.request, totalValue: total.request, name: "Requests" }
        ])
      })
    })
  },[])

  const StyledTableHead = styled(TableHead)({
    backgroundColor: '#e4e4e4'
  })

  const safeDivide = (a: number | undefined, b: number | undefined): number | undefined => (b != 0 && a != undefined && b != undefined ? a / b : undefined)

  const exportToCsv = () =>
    axios.get(`/km/api/km/chats?company_id=${companyId}`, { responseType: "blob" }).then((response) => {
      FileSaver.saveAs(new Blob([response.data], { type: "text/csv" }), "chats.csv")
    })

  const metricsTable = (metrics: MetricAggregationDto[], onExportClick?: () => void) => 
    <Stack spacing={1}>
      {onExportClick && <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={onExportClick}
        >
          Export
        </Button>
      </Stack>}
      <Table>
        <StyledTableHead>
          <TableRow>
            <TableCell width="60%">Metric</TableCell>
            <TableCell width="20%" align="right">Last 30 days</TableCell>
            <TableCell width="20%" align="right">Total</TableCell>
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {metrics.map((metric) => (
            <TableRow key={metric.name}>
              <TableCell>{metric.name}</TableCell>
              <TableCell align="right">{metric.recentValue?.toFixed(2)}</TableCell>
              <TableCell align="right">{metric.totalValue?.toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>

  return (
    <Stack direction="column" spacing={2}>
      <Card>
        <CardContent>
          {metricsTable(tabletVisitMetrics)}
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          {metricsTable(metrics)}
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          {metricsTable(kmChatStats, exportToCsv)}
        </CardContent>
      </Card>
    </Stack>
  )
}


export default MetricsTable
